import React, { useState } from 'react';
import { SplitLayout } from '../components/SplitLayout';
import { SectionsOverview } from '../components/SectionsOverview';
import { PersonalInfoJourney } from '../components/journeys/PersonalInfoJourney';
import { BusinessInfoJourney } from '../components/journeys/BusinessInfoJourney';
import { DocumentsJourney } from '../components/journeys/DocumentsJourney';
import { BankAccountJourney } from '../components/journeys/BankAccountJourney';
import { PhoneEntry } from '../components/PhoneEntry';
import { OTPVerification } from '../components/OTPVerification';
import { Button } from '../components/ui/Button';
import { Clock, RefreshCw } from 'lucide-react';
// Review in progress component
const ReviewInProgressSection = () => <div className="flex flex-col items-center justify-center h-full py-16 px-8 animate-in fade-in duration-500" data-id="element-543">
    <div className="text-center max-w-md" data-id="element-544">
      {/* Icon */}
      <div className="mb-6 inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-full" data-id="element-545">
        <Clock className="w-10 h-10 text-blue-600" data-id="element-546" />
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-547">
        Review in Progress ⏳
      </h2>

      {/* Message */}
      <p className="text-gray-600 mb-2" data-id="element-548">
        Thank you for completing your application!
      </p>
      <p className="text-gray-600 mb-8" data-id="element-549">
        Our team is reviewing your information. We'll get back to you within{' '}
        <span className="font-semibold text-gray-900" data-id="element-550">2-3 business days</span>.
      </p>

      {/* Status card */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8" data-id="element-551">
        <div className="flex items-center justify-between" data-id="element-552">
          <span className="text-sm text-gray-500" data-id="element-553">Application Status</span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800" data-id="element-554">
            Under Review
          </span>
        </div>
      </div>

      {/* Button */}
      <Button className="w-full sm:w-auto" data-id="element-555">
        <RefreshCw className="w-4 h-4 mr-2" data-id="element-556" />
        Check Status
      </Button>
    </div>
  </div>;
type ViewState = 'phone-entry' | 'otp-verification' | 'overview' | 'personal-info' | 'business-info' | 'documents' | 'bank-account' | 'review';
export function OnboardingFlow() {
  const [currentView, setCurrentView] = useState<ViewState>('phone-entry');
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [personalInfoData, setPersonalInfoData] = useState<any>(null);
  const [businessInfoData, setBusinessInfoData] = useState<any>(null);
  const [documentsData, setDocumentsData] = useState<any>(null);
  const [bankAccountData, setBankAccountData] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const handleStartSection = (sectionId: string) => {
    setCurrentView(sectionId as ViewState);
  };
  const handlePersonalInfoComplete = (data: any) => {
    setPersonalInfoData(data);
    setCompletedSections(prev => [...prev, 'personal-info']);
    setCurrentView('overview');
  };
  const handleBusinessInfoComplete = (data: any) => {
    setBusinessInfoData(data);
    setCompletedSections(prev => [...prev, 'business-info']);
    setCurrentView('overview');
  };
  const handleDocumentsComplete = () => {
    setCompletedSections(prev => [...prev, 'documents']);
    setCurrentView('overview');
  };
  const handleBankAccountComplete = () => {
    setCompletedSections(prev => [...prev, 'bank-account']);
    setCurrentView('overview');
  };
  const handleBackToOverview = () => {
    setCurrentView('overview');
  };
  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setCurrentView('otp-verification');
  };
  const handleOTPVerify = () => {
    setIsPhoneVerified(true);
    setCurrentView('overview');
  };
  const handleBackToPhoneEntry = () => {
    setCurrentView('phone-entry');
  };
  // Illustration content changes based on view
  const getIllustrationContent = () => {
    switch (currentView) {
      case 'phone-entry':
        return {
          title: 'Secure Account Access',
          description: 'We use phone verification to keep your account safe and ensure only you can access it.'
        };
      case 'otp-verification':
        return {
          title: 'Verify Your Identity',
          description: 'Enter the code we sent to your phone to confirm your identity and proceed.'
        };
      case 'personal-info':
        return {
          title: "Let's get to know you",
          description: 'We need your personal details to verify your identity and keep your account secure.'
        };
      case 'business-info':
        return {
          title: 'Tell us about your business',
          description: 'We need to understand your business structure and activity to set up your account correctly.'
        };
      case 'documents':
        return {
          title: 'Upload your documents',
          description: 'We need to verify your identity and business with supporting documents to complete your account setup.'
        };
      case 'bank-account':
        return {
          title: 'Link your bank account',
          description: 'Connect your business bank account to receive payments and manage your finances.'
        };
      case 'review':
        return {
          title: 'Application Under Review',
          description: 'Your application has been submitted successfully. Our team is carefully reviewing your information to ensure everything is in order.'
        };
      case 'overview':
      default:
        return {
          title: 'Welcome to Onboarding',
          description: 'Complete these steps to get your account fully set up and ready to go.'
        };
    }
  };
  const illustration = getIllustrationContent();
  return <SplitLayout title={illustration.title} description={illustration.description} data-id="element-557">
      <div className="h-full" data-id="element-558">
        {currentView === 'phone-entry' && <PhoneEntry onContinue={handlePhoneSubmit} data-id="element-559" />}

        {currentView === 'otp-verification' && <OTPVerification phone={phoneNumber} onVerify={handleOTPVerify} onBack={handleBackToPhoneEntry} data-id="element-560" />}

        {currentView === 'overview' && <SectionsOverview completedSections={completedSections} onStartSection={handleStartSection} data-id="element-561" />}

        {currentView === 'personal-info' && <PersonalInfoJourney onComplete={handlePersonalInfoComplete} onExit={handleBackToOverview} data-id="element-562" />}

        {currentView === 'business-info' && <BusinessInfoJourney onComplete={handleBusinessInfoComplete} onExit={handleBackToOverview} data-id="element-563" />}

        {currentView === 'documents' && <DocumentsJourney onComplete={handleDocumentsComplete} data-id="element-564" />}

        {currentView === 'bank-account' && <BankAccountJourney onComplete={handleBankAccountComplete} onExit={handleBackToOverview} data-id="element-565" />}

        {currentView === 'review' && <ReviewInProgressSection data-id="element-566" />}
      </div>
    </SplitLayout>;
}